import React, { useCallback, useRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { getBoundActions } from '../../actions/index'
import { makeIcon, OraclePillButton } from '../OracleCommons/index'
import { usePostHogEvent } from '../PostHog/index'
import { AI_MESSAGE_SENT_GA4_EVENT } from '../../services/event-tracking/index'
import { useVideoPromptSuggestions, useConceptMomentPrompts } from './hooks'
import type { ConceptMoment } from './hooks/useConceptMoments'
import { useVideoState } from '../OracleCommons/useVideoState'

export interface OracleGuideOnSideVideoPromptsProps {
  chatSessionId?: string
  sendMessage?: (message: string) => void
  isStreaming?: boolean
  activeMoment?: ConceptMoment | null
}

/**
 * Pure UI component for a video prompt suggestion button.
 * No analytics knowledge - just renders and calls onClick.
 *
 * Note: Uses shared OraclePillButton but keeps oracle-guide-on-side-layout__summary_btn
 * class for high-specificity CSS that overrides video.js styles.
 */
const VideoPromptBtn = ({
  promptText,
  onClick,
}: {
  promptText: string
  onClick: () => void
}) => {
  return (
    <OraclePillButton
      className="oracle-guide-on-side-layout__summary_btn"
      onClick={onClick}
    >
      {promptText}
      {makeIcon('send-chat-prompt')}
    </OraclePillButton>
  )
}

export const OracleGuideOnSideVideoPrompts = (
  {
    sendMessage, isStreaming, activeMoment,
  }: OracleGuideOnSideVideoPromptsProps,
) => {
  const dispatch = useDispatch()
  const { setAiGa4Event } = getBoundActions(dispatch).eventTracking
  const { suggestions: genericSuggestions, refetch: genericRefetch } = useVideoPromptSuggestions()
  const {
    suggestions: momentSuggestions, refetch: momentRefetch, clear: momentClear,
  } = useConceptMomentPrompts(activeMoment ?? null)
  const trackMessageSent = usePostHogEvent('ai_message_sent')
  const { contentId, featureMediaId } = useVideoState()

  // Moment prompts persist after leaving a moment window, so gate only
  // on whether we have moment suggestions — not on activeMoment
  const useMomentPrompts = !!momentSuggestions?.length
  const suggestions = useMomentPrompts ? momentSuggestions : genericSuggestions
  const promptInputMethod = 'Prompt' as const

  // Track the last clicked prompt to prevent immediate duplicates
  // This is particularly important at the beginning of videos where content chunks overlap
  const lastClickedPrompt = useRef<string | null>(null)

  // Clear the last clicked prompt when video changes
  useEffect(() => {
    lastClickedPrompt.current = null
  }, [contentId, featureMediaId])

  // Create click handler that combines analytics + message sending + refresh
  const createClickHandler = useCallback((promptText: string) => () => {
    // PostHog tracking
    trackMessageSent(promptText, promptInputMethod, 'Prompt', 'click')
    // GA4 tracking
    setAiGa4Event(AI_MESSAGE_SENT_GA4_EVENT.mergeDeep({
      ui_element: 'Prompt',
      input_method: 'prompt',
    }))
    sendMessage?.(promptText)

    // Store the clicked prompt and exclude it from next fetch
    lastClickedPrompt.current = promptText
    const exclude = lastClickedPrompt.current ? [lastClickedPrompt.current] : undefined

    if (activeMoment) {
      // Inside a moment: refetch moment-scoped prompts
      momentRefetch(exclude)
    } else {
      // Outside a moment: clear persisted moment prompts, fall back to generic
      momentClear()
      genericRefetch(exclude)
    }
  }, [trackMessageSent, promptInputMethod, sendMessage, activeMoment,
    momentRefetch, momentClear, genericRefetch, setAiGa4Event])

  // early returns for cases where we don't want to show the prompts
  if (isStreaming) {
    return null
  }
  // Judgement call: do we show prompts for fresh conversations with no messages?
  // if (!chatSessionId) { return null }
  if (!sendMessage) {
    return null
  }
  if (!suggestions?.length) {
    return null
  }

  return (
    <div className="oracle-guide-on-side-layout__prompt_button_container">
      {suggestions.slice(0, 2).map((suggestion) => (
        <VideoPromptBtn
          key={suggestion.prompt}
          promptText={suggestion.prompt}
          onClick={createClickHandler(suggestion.prompt)}
        />
      ))}
    </div>
  )
}
